import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AddIcon } from "@/components/icons/action-icons";
import { EditAction, DeleteAction } from "@/components/table/actions";
import {
  useDeleteProjectOKRMutation,
  useGetAllProjectOKRsQuery,
} from "@/services/(company)/api.projects";
import { useMemo } from "react";
import { PROJECT_TAB_KEYS } from "@/routes/company/routes.projects.constants";
import { useProjectTabParams } from "../params";
import {
  ENUM_PROJECT_OKR_STATUSES,
  type ISingleOKR,
} from "@/types/(company)/projects.interface";
import { Table, type ColumnDef } from "@/components/table";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useConfirm } from "@/context/Confirm";
import { PATHS } from "@/routes/paths";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useLocale } from "@/hooks/useLocale";

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  {
    key: "name",
    label: t("projects:objective"),
  },
  {
    key: "projectKeyResults",
    label: t("projects:okrs"),
  },
  {
    key: "description",
    label: t("projects:description"),
  },
  {
    key: "createdAt",
    label: t("projects:created_date"),
  },
  {
    key: "status",
    label: t("projects:status"),
  },
  {
    key: "actions",
    label: t("projects:actions"),
  },
];

export const ProjectOKRTable = ({ projectId }: { projectId: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Search and page live in the URL, namespaced per tab — see useTableParams.
  const tp = useProjectTabParams(PROJECT_TAB_KEYS.Okrs);
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();

  const { data: { data: okrs, meta } = {}, isFetching } =
    useGetAllProjectOKRsQuery({
      projectId,
      params: { page: tp.page, limit: 10, search: tp.committedSearch },
    });

  const okrById = useMemo(() => new Map(okrs?.map((o) => [o.id, o])), [okrs]);

  const rows =
    okrs?.map((okr) => ({
      id: okr.id,
      name: (
        <Link
          to={PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_OKR(projectId, okr.id)}
        >
          {okr.name}
        </Link>
      ),
      projectKeyResults: okr.projectKeyResults.length
        .toString()
        .padStart(2, "0"),
      description: <p className="max-w-md truncate">{okr.description}</p>,
      createdAt: formatDate(new Date(okr.createdAt), "dd MMM yyyy"),
      status: <OKRStatus status={okr.status} />,
      actions: <RowActions projectId={projectId} okr={okr} />,
    })) ?? [];

  return (
    <Table
      data={rows}
      loading={isFetching}
      searchText={tp.search}
      columns={buildColumns(t)}
      noPadding
      pagination={{
        totalPages: meta?.totalPages ?? 1,
        currentPage: tp.page,
        onPageChange: tp.setPage,
      }}
      onSearchChange={tp.setSearch}
      searchPlaceholder={t("projects:search_by_team_name")}
      actions={[
        ...(can(PLATFORM_SECTION_ENUM.PROJECTS, "create")
          ? [
              {
                type: "link" as const,
                key: "add",
                label: t("projects:add_new"),
                icon: <AddIcon className="size-4" />,
                variant: "primary" as const,
                href: PATHS.COMPANY.COMPANY_PROJECTS_PATHS.ADD_OKR(projectId),
              },
            ]
          : []),
      ]}
      onRowClick={(row) => {
        const okr = okrById.get(row.id as number);
        if (okr)
          navigate(
            PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW_OKR(projectId, okr.id),
            { state: { okr } },
          );
      }}
    />
  );
};

const RowActions = ({
  projectId,
  okr,
}: {
  projectId: string;
  okr: ISingleOKR;
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const confirm = useConfirm();
  const { can } = usePermissions();
  const [deleteKPI, { isLoading: isDeleting }] = useDeleteProjectOKRMutation();

  const handleDeleteKPI = async (okrId: string) => {
    confirm({
      title: t("projects:delete_okr"),
      msg: t("projects:are_you_sure_you_want_to_delete_this_okr"),
      onConfirm: async () => {
        await deleteKPI({ projectId, okrId })
          .unwrap()
          .then((res) => toast.success(res.message))
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      },
    });
  };

  return (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      {can(PLATFORM_SECTION_ENUM.PROJECTS, "edit") && (
        <Link
          to={PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_OKR(projectId, okr.id)}
        >
          <EditAction tooltip={t("projects:edit")} />
        </Link>
      )}
      <DeleteAction
        tooltip={t("projects:delete_okr")}
        disabled={isDeleting || !can(PLATFORM_SECTION_ENUM.PROJECTS, "delete")}
        onClick={() => handleDeleteKPI(okr.id.toString())}
      />
    </div>
  );
};

const OKRStatus = ({ status }: { status: ENUM_PROJECT_OKR_STATUSES }) => {
  const { t } = useTranslation("projects");
  const colors = {
    [ENUM_PROJECT_OKR_STATUSES.COMPLETED]: "text-green-500",
    [ENUM_PROJECT_OKR_STATUSES.IN_PROGRESS]: "text-purple-500",
    [ENUM_PROJECT_OKR_STATUSES.TODO]: "text-pink-500",
  };
  return (
    <p className={`capitalize font-semibold ${colors[status]}`}>{t(status)}</p>
  );
};
